<template>
    <div class="pb2-canvas">
        <div ref="stage" class="pb2-canvas__stage">
            <div class="pb2-canvas__viewport pb2-canvas__sheet" :style="viewportStyle">
                <iframe
                    ref="frame"
                    class="pb2-canvas__frame"
                    :src="previewUrl"
                    :style="frameStyle"
                    :title="t('preview_title')"
                ></iframe>

                <BlockToolbar />
                <CaptureLayer />
                <CanvasEmptyState />

                <Transition name="pb2-skeleton">
                    <div v-if="chrome?.state.value === 'loading'" class="pb2-canvas__skeleton" aria-hidden="true">
                        <span class="pb2-canvas__skeleton-bar pb2-canvas__skeleton-bar--hero"></span>
                        <span class="pb2-canvas__skeleton-bar pb2-canvas__skeleton-bar--line"></span>
                        <span class="pb2-canvas__skeleton-bar pb2-canvas__skeleton-bar--line pb2-canvas__skeleton-bar--short"></span>
                        <span class="pb2-canvas__skeleton-cols">
                            <span class="pb2-canvas__skeleton-bar pb2-canvas__skeleton-bar--col"></span>
                            <span class="pb2-canvas__skeleton-bar pb2-canvas__skeleton-bar--col"></span>
                        </span>
                    </div>
                </Transition>

                <div v-if="chrome?.state.value === 'error'" class="pb2-canvas__error">
                    <span>{{ t('preview_error') }}</span>
                    <button type="button" class="pb2-canvas__error-retry" @click="chrome?.reload()">
                        {{ t('reload_preview') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { useBuilderHost, useBuilderT } from '@src/Common/Builder/host';
import BlockToolbar from '@src/Common/Builder/Canvas/BlockToolbar.vue';
import CanvasEmptyState from '@src/Common/Builder/Canvas/CanvasEmptyState.vue';
import CaptureLayer from '@src/Common/Builder/Canvas/CaptureLayer.vue';
import { useBlockContextMenuOptional } from '@src/Common/Builder/useBlockContextMenu';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { useBuilderBridge } from '@src/Common/Builder/useBuilderBridge';
import { breakpointOptions, useCanvasChromeOptional } from '@src/Common/Builder/useCanvasChrome';

const t = useBuilderT();

const host = useBuilderHost();
const store = useBuilderStore();
const bridge = useBuilderBridge();
const chrome = useCanvasChromeOptional();
const frame = bridge.iframe;

const previewUrl = computed(() => chrome?.previewUrl.value ?? host.preview.previewUrl(0));

const frameHeight = computed(() => Math.max(store.documentHeight.value, 400));

const stage = ref<HTMLElement | null>(null);
const stageWidth = ref(0);
let stageObserver: ResizeObserver | null = null;

/** Explicit canvas width of the active breakpoint; null = fluid desktop. */
const breakpointWidth = computed<number | null>(() => {
    const widths = chrome?.breakpoints.value ?? { tablet: 768, mobile: 375 };
    const option = breakpointOptions(t, widths).find((candidate) => candidate.key === store.breakpoint.value);

    return option ? option.width : null;
});

const scale = computed(() => {
    const width = breakpointWidth.value;

    if (width === null || stageWidth.value <= 0) {
        return 1;
    }

    return Math.min(1, stageWidth.value / width);
});

watchEffect(() => {
    store.canvasScale.value = scale.value;
});

const frameStyle = computed(() => {
    const width = breakpointWidth.value;

    if (width === null) {
        return { height: `${frameHeight.value}px` };
    }

    return {
        width: `${width}px`,
        height: `${frameHeight.value}px`,
        transform: `scale(${scale.value})`,
        transformOrigin: 'top left',
    };
});

/** Sized to the visual (scaled) box — transforms do not affect layout. */
const viewportStyle = computed(() => {
    const width = breakpointWidth.value;

    if (width === null) {
        return {};
    }

    return {
        width: `${Math.round(width * scale.value)}px`,
        height: `${Math.round(frameHeight.value * scale.value)}px`,
    };
});

const contextMenu = useBlockContextMenuOptional();

/** Bridge coordinates are iframe-viewport and unscaled; the iframe's own
 *  bounding rect already reflects the breakpoint transform, so scaling the
 *  coordinate and offsetting by the rect lands in parent viewport space. */
const toParentPoint = (x: number, y: number): { x: number; y: number } | null => {
    const rect = bridge.iframe.value?.getBoundingClientRect();

    if (!rect) {
        return null;
    }

    const factor = store.canvasScale.value || 1;

    return { x: rect.left + x * factor, y: rect.top + y * factor };
};

watch(
    () => bridge.contextMenuRequest.value,
    (request) => {
        if (request === null || contextMenu === null) {
            return;
        }

        const point = toParentPoint(request.x, request.y);

        if (point === null) {
            return;
        }

        const tableAttr = store.tables.value[request.id]?.attr;
        contextMenu.open(point, request.id, {
            surface: 'canvas',
            tableCell: request.cell && tableAttr ? { attr: tableAttr, ...request.cell } : null,
        });
    },
);

watch(
    () => bridge.pageContextMenuRequest.value,
    (request) => {
        if (request === null || contextMenu === null) {
            return;
        }

        const point = toParentPoint(request.x, request.y);

        if (point !== null) {
            contextMenu.openPage(point);
        }
    },
);

onMounted(() => {
    if (stage.value) {
        stageObserver = new ResizeObserver((entries) => {
            stageWidth.value = entries[0]?.contentRect.width ?? 0;
        });
        stageObserver.observe(stage.value);
        stageWidth.value = stage.value.clientWidth;
    }
});

onBeforeUnmount(() => {
    if (stageObserver) {
        stageObserver.disconnect();
    }
});
</script>

<style scoped>
.pb2-canvas {
    display: flex;
    flex-direction: column;
}

/* ============ stage — gallery backdrop, sheet of paper ============ */

.pb2-canvas__stage {
    position: relative;
    /* Edge-to-edge (F12): no stage padding — content spans the full canvas.
       Centering of fixed-width breakpoint viewports is handled by
       .pb2-canvas__viewport margin-inline: auto. */
    padding: 0;
    background: var(--pb2-stage-bg, #f0f0f2);
    /* the fixed-width breakpoint viewport must not inflate ancestors with
       min-width: min-content — without this the stage can never be narrower
       than the breakpoint and the fit scale never engages */
    contain: inline-size;
}

.pb2-canvas__viewport {
    position: relative;
    width: 100%;
    margin-inline: auto;
}

.pb2-canvas__sheet {
    border-radius: 2px;
    box-shadow: var(--pb2-sheet-shadow, 0 24px 64px rgb(0 0 0 / 0.16), 0 2px 8px rgb(0 0 0 / 0.08));
    background: var(--pb2-surface-raised, #fff);
}

.pb2-canvas__frame {
    display: block;
    width: 100%;
    min-height: 400px;
    border: 0;
    border-radius: 2px;
    background: var(--pb2-surface-raised, #fff);
}

/* ============ loading skeleton — pod CaptureLayer, čistě vizuální ============ */

.pb2-canvas__skeleton {
    position: absolute;
    inset: 0;
    z-index: 15;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 2rem;
    background: var(--pb2-surface-raised, #fff);
    border-radius: 2px;
    pointer-events: none;
    overflow: hidden;
}

.pb2-canvas__skeleton-bar {
    position: relative;
    display: block;
    border-radius: 4px;
    background: color-mix(in srgb, var(--pb2-border, #e4e4e7) 60%, transparent);
    overflow: hidden;
}

.pb2-canvas__skeleton-bar::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgb(255 255 255 / 0.5), transparent);
    animation: pb2-skeleton-sweep 1.4s ease-in-out infinite;
}

.pb2-canvas__skeleton-bar--hero {
    height: 120px;
}

.pb2-canvas__skeleton-bar--line {
    height: 14px;
    max-width: 70%;
}

.pb2-canvas__skeleton-bar--short {
    max-width: 45%;
}

.pb2-canvas__skeleton-cols {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.25rem;
}

.pb2-canvas__skeleton-bar--col {
    flex: 1;
    height: 90px;
}

@keyframes pb2-skeleton-sweep {
    to {
        transform: translateX(100%);
    }
}

.pb2-skeleton-leave-active {
    transition: opacity 200ms ease;
}

.pb2-skeleton-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .pb2-canvas__skeleton-bar::after {
        animation: none;
    }

    .pb2-skeleton-leave-active {
        transition: none;
    }
}

/* ============ preview error — retry přímo na plátně ============ */

.pb2-canvas__error {
    position: absolute;
    inset: 0;
    z-index: 25;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.71875rem;
    color: var(--pb2-muted, #a1a1aa);
    background: var(--pb2-surface-raised, #fff);
    border-radius: 2px;
}

.pb2-canvas__error-retry {
    height: 26px;
    padding: 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
    background: color-mix(in srgb, var(--pb2-border, #e4e4e7) 55%, transparent);
    border: 0;
    border-radius: 5px;
    cursor: pointer;
}
</style>
